import { ArrowUpDown, MoreHorizontal } from "lucide-react"
import { Checkbox } from "@radix-ui/react-checkbox"
import RadixButton from "../ui/RadixButton"
import RadixDropdownMenu from "../ui/RadixDropdownMenu"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { getStatusTailwindClasses } from "../../utils/statusUtils"
import { getTicketSourceTailwindClasses, normalizeTicketSource } from "../../utils/ticketSourceUtils"

dayjs.extend(utc)

// Helper function to format date and time
const formatDateTime = (dateString) => {
  if (!dateString) return { date: 'N/A', time: 'N/A' };
  
  const date = dayjs.utc(dateString);
  return {
    date: date.format('DD/MM/YYYY'),
    time: date.format('HH:mm')
  };
};

// Use centralized status color utility - getStatusTailwindClasses from utils/statusUtils

// This type defines the shape of our trip data
export const tripColumns = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
    size: 30,
  },
  {
    accessorKey: "userId",
    header: ({ column }) => {
      return (
        <RadixButton
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-6 px-1 text-xs"
        >
          User ID
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </RadixButton>
      )
    },
    cell: ({ row }) => (
      <div className="font-medium text-xs truncate">{row.getValue("userId")}</div>
    ),
    size: 60,
  },
  {
    accessorKey: "ticketId",
    header: ({ column }) => {
      return (
        <RadixButton
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-6 px-1 text-xs"
        >
          Ticket ID
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </RadixButton>
      )
    },
    cell: ({ row }) => (
      <div className="font-medium text-xs truncate">{row.getValue("ticketId")}</div>
    ),
    size: 80,
  },
  {
    accessorKey: "email",
    header: "Email",
    cell: ({ row }) => {
      const email = row.getValue("email")
      return (
        <div className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-800 max-w-[120px] truncate">
          {email || "N/A"}
        </div>
      )
    },
    size: 120,
  },
  {
    id: "sector",
    header: "Sector",
    accessorFn: (row) => `${row.departureIata || 'N/A'}_${row.arrivalIata || 'N/A'}`,
    cell: ({ row }) => {
      const departure = row.original.departureIata || "N/A";
      const arrival = row.original.arrivalIata || "N/A";
      return (
        <div className="text-xs font-medium truncate">
          {departure !== "N/A" && arrival !== "N/A" ? `${departure}_${arrival}` : "N/A"}
        </div>
      );
    },
    size: 70,
  },
  {
    accessorKey: "departureDate",
    header: ({ column }) => {
      return (
        <RadixButton
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-6 px-1 text-xs"
        >
          Departure
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </RadixButton>
      )
    },
    cell: ({ row }) => {
      const departureDateTime = formatDateTime(row.getValue("departureDate"))
      return (
        <div className="text-xs">
          <div className="font-medium truncate">{departureDateTime.date}</div>
          <div className="text-gray-500 truncate">{departureDateTime.time}</div>
        </div>
      )
    },
    size: 90,
  },

  {
    accessorKey: "checkInStatus",
    header: "Check-In Status",
    cell: ({ row }) => {
      const status = row.getValue("checkInStatus") || 'NONE'
      const colorClass = getStatusTailwindClasses(status)
      return (
        <div className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${colorClass} max-w-[80px] truncate`}>
          {status}
        </div>
      )
    },
    size: 80,
  },

  {
    accessorKey: "ticketSource",
    header: "Source",
    cell: ({ row }) => {
      const source = row.getValue("ticketSource")
      const colorClass = getTicketSourceTailwindClasses(source)
      const normalizedSource = normalizeTicketSource(source)
      return (
        <div className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${colorClass} max-w-[70px] truncate`}>
          {normalizedSource}
        </div>
      )
    },
    size: 70,
  },
  {
    id: "actions",
    enableHiding: false,
    size: 40,
    cell: ({ row, table }) => {
      const trip = row.original

      const menuItems = [
        {
          label: "Actions",
          type: "label"
        },
        {
          label: "View trip details",
          onClick: () => {
            // Get the onTripClick function from table meta
            const onTripClick = table.options.meta?.onTripClick
            if (onTripClick) {
              onTripClick(trip)
            }
          }
        },
        {
          label: "Copy ticket ID",
          onClick: () => navigator.clipboard.writeText(trip.ticketId.toString())
        },
        {
          label: "Copy user ID",
          onClick: () => navigator.clipboard.writeText(trip.userId.toString())
        }
      ]

      return (
        <RadixDropdownMenu
          trigger={
            <RadixButton variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </RadixButton>
          }
          items={menuItems}
          align="end"
        />
      )
    },
  },
]
